import { Skeleton, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { Text, type api, api as apiClient, getQueryProps, isDefined } from 'common';

function extractVidyardUuid(videoUrl: string): string {
  const shareMatch = /^https?:\/\/(?:[\w-]+\.)*vidyard\.com\/watch\/([a-zA-Z0-9_-]+)/.exec(
    videoUrl
  );
  if (isDefined(shareMatch)) {
    return shareMatch[1];
  }
  const playMatch = /^https?:\/\/play\.vidyard\.com\/([a-zA-Z0-9_-]+)/.exec(videoUrl);
  if (isDefined(playMatch)) {
    return playMatch[1];
  }
  return '';
}

interface FlowTemplatePreviewDemoVideoProps {
  demoVideo?: api.FlowDefinitionTemplate.DemoVideo;
}

export function FlowTemplatePreviewDemoVideo(props: FlowTemplatePreviewDemoVideoProps) {
  const { demoVideo } = props;
  const videoUrl = demoVideo?.videoUrl;
  const uuid = isDefined(videoUrl) ? extractVidyardUuid(videoUrl) : '';
  const isEnabled = isDefined(videoUrl) && uuid.length > 0;

  const {
    data: tokenResponse,
    isPending: isTokenPending,
    isError: isTokenError,
  } = useQuery({
    ...getQueryProps(apiClient.client.Vidyard, 'getToken', { playerUuid: uuid }),
    enabled: isEnabled,
    staleTime: 30 * 60 * 1000,
    refetchInterval: 45 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  if (!isDefined(demoVideo) || !isDefined(videoUrl)) {
    return null;
  }

  const hasInvalidUrl = uuid.length === 0;
  const hasError = isTokenError || hasInvalidUrl;
  const isLoading = !hasError && isEnabled && isTokenPending;

  const iframeSrc = isDefined(tokenResponse)
    ? `https://play.vidyard.com/${encodeURIComponent(uuid)}.html?jwt=${encodeURIComponent(tokenResponse.token)}`
    : undefined;

  return (
    <Stack gap={1}>
      <Text variant="heading4" bold>
        Demo Video
      </Text>
      {hasError ? (
        <Stack
          sx={{
            width: '100%',
            height: 360,
            backgroundColor: 'grey.100',
            borderRadius: 1,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text variant="body1" color="text.secondary">
            Unable to load video
          </Text>
        </Stack>
      ) : isLoading || !isDefined(iframeSrc) ? (
        <Skeleton variant="rectangular" width="100%" height={360} sx={{ borderRadius: 1 }} />
      ) : (
        <iframe
          src={iframeSrc}
          title="Demo Video"
          // allow-same-origin is required for playback (player needs cookies/localStorage).
          // This is only a sandbox escape when the frame is same-origin with the embedder.
          // The src is a hardcoded cross-origin literal (play.vidyard.com) and uuid is
          // restricted to [a-zA-Z0-9_-], so this frame can never resolve to our origin.
          sandbox="allow-scripts allow-same-origin"
          allow="fullscreen"
          referrerPolicy="no-referrer"
          loading="lazy"
          style={{
            width: '100%',
            aspectRatio: '16 / 9',
            border: 'none',
            borderRadius: 8,
          }}
        />
      )}
    </Stack>
  );
}
